/**
 * Índice de Handlers
 * Exporta todos os handlers do SDK
 */

export { ErrorHandler } from './ErrorHandler.js';

/**
 * Handler de Eventos
 * Gerencia eventos personalizados do SDK
 */
export class EventHandler {
    constructor(logger) {
        this.logger = logger ? logger.child('EventHandler') : console;
        this.listeners = new Map();
        this.eventHistory = [];
        this.maxHistorySize = 1000;
        
        this.stats = {
            eventsEmitted: 0,
            listenersRegistered: 0,
            listenersRemoved: 0,
            errorsInListeners: 0
        };
    }
    
    /**
     * Registra listener para evento
     */
    on(eventName, listener, options = {}) {
        if (typeof listener !== 'function') {
            throw new Error('Listener deve ser uma função');
        }
        
        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, []);
        }
        
        const listenerConfig = {
            fn: listener,
            once: options.once || false,
            priority: options.priority || 0,
            id: this.generateListenerId(),
            registeredAt: Date.now()
        };
        
        this.listeners.get(eventName).push(listenerConfig);
        
        // Ordena por prioridade (maior prioridade primeiro)
        this.listeners.get(eventName).sort((a, b) => b.priority - a.priority);
        
        this.stats.listenersRegistered++;
        
        this.logger.debug('Listener registrado', {
            eventName,
            listenerId: listenerConfig.id,
            priority: listenerConfig.priority,
            once: listenerConfig.once
        });
        
        return listenerConfig.id;
    }
    
    /**
     * Registra listener que executa apenas uma vez
     */
    once(eventName, listener, options = {}) {
        return this.on(eventName, listener, { ...options, once: true });
    }
    
    /**
     * Remove listener
     */
    off(eventName, listenerIdOrFunction) {
        if (!this.listeners.has(eventName)) {
            return false;
        }
        
        const listeners = this.listeners.get(eventName);
        let removed = false;
        
        if (typeof listenerIdOrFunction === 'string') {
            // Remove por ID
            const index = listeners.findIndex(l => l.id === listenerIdOrFunction);
            if (index > -1) {
                listeners.splice(index, 1);
                removed = true;
            }
        } else if (typeof listenerIdOrFunction === 'function') {
            // Remove por função
            const index = listeners.findIndex(l => l.fn === listenerIdOrFunction);
            if (index > -1) {
                listeners.splice(index, 1);
                removed = true;
            }
        }
        
        if (removed) {
            this.stats.listenersRemoved++;
            this.logger.debug('Listener removido', { eventName });
            
            // Remove array vazio
            if (listeners.length === 0) {
                this.listeners.delete(eventName);
            }
        }
        
        return removed;
    }
    
    /**
     * Remove todos os listeners de um evento
     */
    removeAllListeners(eventName) {
        if (eventName) {
            const count = this.listeners.has(eventName) ? this.listeners.get(eventName).length : 0;
            this.listeners.delete(eventName);
            this.stats.listenersRemoved += count;
            this.logger.debug('Todos os listeners removidos', { eventName, count });
            return count;
        } else {
            // Remove todos os listeners
            let totalCount = 0;
            for (const listeners of this.listeners.values()) {
                totalCount += listeners.length;
            }
            this.listeners.clear();
            this.stats.listenersRemoved += totalCount;
            this.logger.debug('Todos os listeners removidos', { count: totalCount });
            return totalCount;
        }
    }
    
    /**
     * Emite evento
     */
    emit(eventName, data = null, options = {}) {
        const event = {
            name: eventName,
            data,
            timestamp: Date.now(),
            id: this.generateEventId(),
            source: options.source || 'unknown'
        };
        
        // Adiciona ao histórico
        this.addToHistory(event);
        
        this.stats.eventsEmitted++;
        
        this.logger.debug('Evento emitido', {
            eventName,
            eventId: event.id,
            hasData: data !== null,
            source: event.source
        });
        
        if (!this.listeners.has(eventName)) {
            return 0; // Nenhum listener
        }
        
        const listeners = [...this.listeners.get(eventName)];
        let executedCount = 0;
        
        for (const listenerConfig of listeners) {
            try {
                // Executa listener
                if (options.async) {
                    // Execução assíncrona (não bloqueia)
                    this.executeListenerAsync(listenerConfig, event);
                } else {
                    // Execução síncrona
                    listenerConfig.fn(event);
                }
                
                executedCount++;
                
                // Remove listener se for 'once'
                if (listenerConfig.once) {
                    this.off(eventName, listenerConfig.id);
                }
                
            } catch (error) {
                this.stats.errorsInListeners++;
                this.logger.error('Erro em listener', {
                    eventName,
                    listenerId: listenerConfig.id,
                    error: error.message
                });
                
                // Emite evento de erro
                if (eventName !== 'listener-error') {
                    this.emit('listener-error', {
                        originalEvent: event,
                        listenerId: listenerConfig.id,
                        error: error.message
                    }, { source: 'EventHandler' });
                }
            }
        }
        
        return executedCount;
    }
    
    /**
     * Executa listener de forma assíncrona
     */
    async executeListenerAsync(listenerConfig, event) {
        try {
            await listenerConfig.fn(event);
        } catch (error) {
            this.stats.errorsInListeners++;
            this.logger.error('Erro em listener assíncrono', {
                eventName: event.name,
                listenerId: listenerConfig.id,
                error: error.message
            });
        }
    }
    
    /**
     * Verifica se tem listeners para um evento
     */
    hasListeners(eventName) {
        return this.listeners.has(eventName) && this.listeners.get(eventName).length > 0;
    }
    
    /**
     * Obtém número de listeners para um evento
     */
    listenerCount(eventName) {
        return this.listeners.has(eventName) ? this.listeners.get(eventName).length : 0;
    }
    
    /**
     * Lista todos os eventos com listeners
     */
    eventNames() {
        return Array.from(this.listeners.keys());
    }
    
    /**
     * Obtém listeners de um evento
     */
    getListeners(eventName) {
        if (!this.listeners.has(eventName)) {
            return [];
        }
        
        return this.listeners.get(eventName).map(l => ({
            id: l.id,
            priority: l.priority,
            once: l.once,
            registeredAt: l.registeredAt
        }));
    }
    
    /**
     * Adiciona evento ao histórico
     */
    addToHistory(event) {
        this.eventHistory.push(event);
        
        // Limita tamanho do histórico
        if (this.eventHistory.length > this.maxHistorySize) {
            this.eventHistory.shift();
        }
    }
    
    /**
     * Obtém histórico de eventos
     */
    getEventHistory(filter = {}) {
        let history = [...this.eventHistory];
        
        if (filter.eventName) {
            history = history.filter(e => e.name === filter.eventName);
        }
        
        if (filter.source) {
            history = history.filter(e => e.source === filter.source);
        }
        
        if (filter.since) {
            history = history.filter(e => e.timestamp >= filter.since);
        }
        
        if (filter.limit) {
            history = history.slice(-filter.limit);
        }
        
        return history;
    }
    
    /**
     * Limpa histórico de eventos
     */
    clearHistory() {
        const count = this.eventHistory.length;
        this.eventHistory = [];
        this.logger.debug('Histórico de eventos limpo', { count });
        return count;
    }
    
    /**
     * Gera ID único para listener
     */
    generateListenerId() {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 9);
        return `listener-${timestamp}-${random}`;
    }
    
    /**
     * Gera ID único para evento
     */
    generateEventId() {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 9);
        return `event-${timestamp}-${random}`;
    }
    
    /**
     * Obtém estatísticas do handler
     */
    getStats() {
        return {
            eventsEmitted: this.stats.eventsEmitted,
            listenersRegistered: this.stats.listenersRegistered,
            listenersRemoved: this.stats.listenersRemoved,
            activeListeners: Array.from(this.listeners.values()).reduce((sum, listeners) => sum + listeners.length, 0),
            eventTypes: this.listeners.size,
            errorsInListeners: this.stats.errorsInListeners,
            historySize: this.eventHistory.length,
            maxHistorySize: this.maxHistorySize
        };
    }
    
    /**
     * Atualiza configuração
     */
    updateConfig(config) {
        if (config.maxHistorySize && typeof config.maxHistorySize === 'number') {
            this.maxHistorySize = config.maxHistorySize;
            
            // Ajusta histórico se necessário
            if (this.eventHistory.length > this.maxHistorySize) {
                this.eventHistory = this.eventHistory.slice(-this.maxHistorySize);
            }
        }
        
        this.logger.debug('Configuração do EventHandler atualizada');
    }
    
    /**
     * Limpa recursos
     */
    destroy() {
        const listenersCount = this.removeAllListeners();
        const historyCount = this.clearHistory();
        
        this.logger.debug('EventHandler destruído', {
            listenersRemoved: listenersCount,
            historyCleared: historyCount
        });
    }
}

/**
 * Handler de Estado
 * Gerencia estado global do SDK
 */
export class StateHandler {
    constructor(logger) {
        this.logger = logger ? logger.child('StateHandler') : console;
        this.state = new Map();
        this.subscribers = new Map();
        this.history = [];
        this.maxHistorySize = 100;
        
        this.stats = {
            stateChanges: 0,
            subscribersNotified: 0,
            subscriptionsCreated: 0,
            subscriptionsRemoved: 0
        };
    }
    
    /**
     * Define valor no estado
     */
    set(key, value, options = {}) {
        const previousValue = this.state.get(key);
        const hasChanged = previousValue !== value;
        
        if (!hasChanged && !options.force) {
            return false;
        }
        
        this.state.set(key, value);
        
        const change = {
            key,
            previousValue,
            newValue: value,
            timestamp: Date.now(),
            source: options.source || 'unknown'
        };
        
        // Adiciona ao histórico
        this.addToHistory(change);
        
        this.stats.stateChanges++;
        
        this.logger.debug('Estado alterado', {
            key,
            hasChanged,
            source: change.source
        });
        
        // Notifica subscribers
        if (hasChanged) {
            this.notifySubscribers(key, change);
        }
        
        return true;
    }
    
    /**
     * Obtém valor do estado
     */
    get(key, defaultValue = undefined) {
        return this.state.has(key) ? this.state.get(key) : defaultValue;
    }
    
    /**
     * Verifica se chave existe no estado
     */
    has(key) {
        return this.state.has(key);
    }
    
    /**
     * Remove chave do estado
     */
    delete(key, options = {}) {
        if (!this.state.has(key)) {
            return false;
        }
        
        const previousValue = this.state.get(key);
        this.state.delete(key);
        
        const change = {
            key,
            previousValue,
            newValue: undefined,
            timestamp: Date.now(),
            source: options.source || 'unknown',
            deleted: true
        };
        
        this.addToHistory(change);
        this.stats.stateChanges++;
        
        this.logger.debug('Estado removido', { key, source: change.source });
        
        // Notifica subscribers
        this.notifySubscribers(key, change);
        
        return true;
    }
    
    /**
     * Limpa todo o estado
     */
    clear(options = {}) {
        const keys = Array.from(this.state.keys());
        this.state.clear();
        
        const change = {
            key: '*',
            previousValue: null,
            newValue: null,
            timestamp: Date.now(),
            source: options.source || 'unknown',
            cleared: true,
            affectedKeys: keys
        };
        
        this.addToHistory(change);
        this.stats.stateChanges++;
        
        this.logger.debug('Estado limpo', { keysCount: keys.length, source: change.source });
        
        // Notifica todos os subscribers
        keys.forEach(key => this.notifySubscribers(key, change));
        
        return keys.length;
    }
    
    /**
     * Subscreve a mudanças em uma chave
     */
    subscribe(key, callback, options = {}) {
        if (typeof callback !== 'function') {
            throw new Error('Callback deve ser uma função');
        }
        
        if (!this.subscribers.has(key)) {
            this.subscribers.set(key, []);
        }
        
        const subscription = {
            id: this.generateSubscriptionId(),
            callback,
            once: options.once || false,
            immediate: options.immediate || false,
            createdAt: Date.now()
        };
        
        this.subscribers.get(key).push(subscription);
        this.stats.subscriptionsCreated++;
        
        this.logger.debug('Subscription criada', {
            key,
            subscriptionId: subscription.id,
            once: subscription.once,
            immediate: subscription.immediate
        });
        
        // Executa imediatamente se solicitado
        if (subscription.immediate && this.state.has(key)) {
            try {
                callback({
                    key,
                    previousValue: undefined,
                    newValue: this.state.get(key),
                    timestamp: Date.now(),
                    source: 'immediate'
                });
            } catch (error) {
                this.logger.error('Erro em callback imediato', {
                    key,
                    subscriptionId: subscription.id,
                    error: error.message
                });
            }
        }
        
        return subscription.id;
    }
    
    /**
     * Remove subscription
     */
    unsubscribe(key, subscriptionIdOrCallback) {
        if (!this.subscribers.has(key)) {
            return false;
        }
        
        const subscriptions = this.subscribers.get(key);
        let removed = false;
        
        if (typeof subscriptionIdOrCallback === 'string') {
            // Remove por ID
            const index = subscriptions.findIndex(s => s.id === subscriptionIdOrCallback);
            if (index > -1) {
                subscriptions.splice(index, 1);
                removed = true;
            }
        } else if (typeof subscriptionIdOrCallback === 'function') {
            // Remove por callback
            const index = subscriptions.findIndex(s => s.callback === subscriptionIdOrCallback);
            if (index > -1) {
                subscriptions.splice(index, 1);
                removed = true;
            }
        }
        
        if (removed) {
            this.stats.subscriptionsRemoved++;
            this.logger.debug('Subscription removida', { key });
            
            // Remove array vazio
            if (subscriptions.length === 0) {
                this.subscribers.delete(key);
            }
        }
        
        return removed;
    }
    
    /**
     * Notifica subscribers de uma chave
     */
    notifySubscribers(key, change) {
        if (!this.subscribers.has(key)) {
            return 0;
        }
        
        const subscriptions = [...this.subscribers.get(key)];
        let notifiedCount = 0;
        
        for (const subscription of subscriptions) {
            try {
                subscription.callback(change);
                notifiedCount++;
                
                // Remove subscription se for 'once'
                if (subscription.once) {
                    this.unsubscribe(key, subscription.id);
                }
                
            } catch (error) {
                this.logger.error('Erro em callback de subscription', {
                    key,
                    subscriptionId: subscription.id,
                    error: error.message
                });
            }
        }
        
        this.stats.subscribersNotified += notifiedCount;
        return notifiedCount;
    }
    
    /**
     * Obtém todas as chaves do estado
     */
    keys() {
        return Array.from(this.state.keys());
    }
    
    /**
     * Obtém todos os valores do estado
     */
    values() {
        return Array.from(this.state.values());
    }
    
    /**
     * Obtém todo o estado como objeto
     */
    getAll() {
        return Object.fromEntries(this.state);
    }
    
    /**
     * Define múltiplos valores no estado
     */
    setMultiple(obj, options = {}) {
        const changes = [];
        
        for (const [key, value] of Object.entries(obj)) {
            if (this.set(key, value, options)) {
                changes.push(key);
            }
        }
        
        return changes;
    }
    
    /**
     * Adiciona mudança ao histórico
     */
    addToHistory(change) {
        this.history.push(change);
        
        // Limita tamanho do histórico
        if (this.history.length > this.maxHistorySize) {
            this.history.shift();
        }
    }
    
    /**
     * Obtém histórico de mudanças
     */
    getHistory(filter = {}) {
        let history = [...this.history];
        
        if (filter.key) {
            history = history.filter(h => h.key === filter.key);
        }
        
        if (filter.source) {
            history = history.filter(h => h.source === filter.source);
        }
        
        if (filter.since) {
            history = history.filter(h => h.timestamp >= filter.since);
        }
        
        if (filter.limit) {
            history = history.slice(-filter.limit);
        }
        
        return history;
    }
    
    /**
     * Limpa histórico
     */
    clearHistory() {
        const count = this.history.length;
        this.history = [];
        this.logger.debug('Histórico de estado limpo', { count });
        return count;
    }
    
    /**
     * Gera ID único para subscription
     */
    generateSubscriptionId() {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 9);
        return `sub-${timestamp}-${random}`;
    }
    
    /**
     * Obtém estatísticas do handler
     */
    getStats() {
        return {
            stateSize: this.state.size,
            stateChanges: this.stats.stateChanges,
            subscribersNotified: this.stats.subscribersNotified,
            subscriptionsCreated: this.stats.subscriptionsCreated,
            subscriptionsRemoved: this.stats.subscriptionsRemoved,
            activeSubscriptions: Array.from(this.subscribers.values()).reduce((sum, subs) => sum + subs.length, 0),
            subscribedKeys: this.subscribers.size,
            historySize: this.history.length,
            maxHistorySize: this.maxHistorySize
        };
    }
    
    /**
     * Atualiza configuração
     */
    updateConfig(config) {
        if (config.maxHistorySize && typeof config.maxHistorySize === 'number') {
            this.maxHistorySize = config.maxHistorySize;
            
            // Ajusta histórico se necessário
            if (this.history.length > this.maxHistorySize) {
                this.history = this.history.slice(-this.maxHistorySize);
            }
        }
        
        this.logger.debug('Configuração do StateHandler atualizada');
    }
    
    /**
     * Limpa recursos
     */
    destroy() {
        const stateSize = this.clear({ source: 'destroy' });
        const historyCount = this.clearHistory();
        
        // Remove todas as subscriptions
        let subscriptionsCount = 0;
        for (const [key, subscriptions] of this.subscribers.entries()) {
            subscriptionsCount += subscriptions.length;
        }
        this.subscribers.clear();
        
        this.logger.debug('StateHandler destruído', {
            stateCleared: stateSize,
            historyCleared: historyCount,
            subscriptionsRemoved: subscriptionsCount
        });
    }
}

/**
 * Constantes de handlers
 */
export const HANDLER_EVENTS = {
    // Eventos do SDK
    SDK_INIT: 'sdk-init',
    SDK_READY: 'sdk-ready',
    SDK_DESTROY: 'sdk-destroy',
    
    // Eventos de coleta
    COLLECTION_START: 'collection-start',
    COLLECTION_END: 'collection-end',
    COLLECTION_ERROR: 'collection-error',
    
    // Eventos de envio
    SEND_START: 'send-start',
    SEND_SUCCESS: 'send-success',
    SEND_ERROR: 'send-error',
    
    // Eventos de comportamento
    BEHAVIORAL_START: 'behavioral-start',
    BEHAVIORAL_STOP: 'behavioral-stop',
    BEHAVIORAL_DATA: 'behavioral-data',
    
    // Eventos de erro
    ERROR: 'error',
    WARNING: 'warning',
    
    // Eventos de configuração
    CONFIG_UPDATE: 'config-update',
    
    // Eventos de estado
    STATE_CHANGE: 'state-change'
};

export const HANDLER_STATES = {
    // Estados do SDK
    SDK_STATUS: 'sdk-status',
    SDK_CONFIG: 'sdk-config',
    
    // Estados de coleta
    COLLECTION_STATUS: 'collection-status',
    LAST_FINGERPRINT: 'last-fingerprint',
    
    // Estados comportamentais
    BEHAVIORAL_ACTIVE: 'behavioral-active',
    BEHAVIORAL_DATA: 'behavioral-data',
    
    // Estados de rede
    NETWORK_STATUS: 'network-status',
    LAST_SEND_TIME: 'last-send-time',
    
    // Estados de erro
    LAST_ERROR: 'last-error',
    ERROR_COUNT: 'error-count'
};